import React from 'react';
import type { SlaPolicyResponse } from '@troia-v3/types';

import { CardActionsMenu, Checkbox, StatusPill, Tooltip } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { CLOCK_KEY_LABELS, formatTargetMs } from '../utils/slaPolicyHelpers';

export interface SlaPolicyListItemProps {
  policy: SlaPolicyResponse;
  /**
   * Posição na ordem de AVALIAÇÃO (1-based), calculada DENTRO do grupo (mesmo
   * `entityType`, só políticas ATIVAS) — vem de `rankPoliciesForDisplay`, não
   * de um índice bruto sobre o array exibido (que mistura tipos e inclui
   * inativas). `0` = não avaliada (a linha renderiza "—").
   */
  position: number;
  /**
   * Motivo pelo qual a posição não pode ser exibida — hoje só "há busca ativa"
   * (a listagem vem filtrada do SERVIDOR, então contar a sequência daria a
   * posição dentro do resultado da busca, não na cascata). Presente = a coluna
   * "#" mostra "—" com este texto no tooltip, mesmo em política ativa.
   */
  positionHiddenReason?: string;
  selected: boolean;
  /** `false` na política padrão — ela é a âncora do fim da cascata. */
  draggable: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  /**
   * Mensagem do tooltip quando `draggable=false` por um motivo que NÃO é
   * "é a padrão" nem "está inativa" — hoje só o caso de >100 políticas ativas
   * (teto do `reorderSlaPoliciesSchema`, B2). Ausente = usa a inferência
   * padrão a partir de `policy.isDefault`/`policy.status`.
   */
  dragDisabledReason?: string;
  onToggleSelect: (id: string) => void;
  onEdit: (policy: SlaPolicyResponse) => void;
  onDelete: (policy: SlaPolicyResponse) => void;
  onSetDefault: (policy: SlaPolicyResponse) => void;
  onDragStart: (e: React.DragEvent<HTMLTableRowElement>, id: string) => void;
  onDragOver: (e: React.DragEvent<HTMLTableRowElement>) => void;
  onDrop: (e: React.DragEvent<HTMLTableRowElement>, id: string) => void;
}

const CELL = 'px-4 py-3 text-sm align-middle';

export const SlaPolicyListItem: React.FC<SlaPolicyListItemProps> = ({
  policy, position, positionHiddenReason, selected, draggable, canUpdate, canDelete,
  dragDisabledReason,
  onToggleSelect, onEdit, onDelete, onSetDefault, onDragStart, onDragOver, onDrop,
}) => {
  // Resumo do que a política promete, em uma linha: "Primeira resposta 15 min
  // úteis · Resolução 24 h úteis". Sem isso o admin precisa abrir o drawer só
  // para lembrar o que cada política faz.
  const summary = policy.targets
    .map((row) => `${CLOCK_KEY_LABELS[row.clockKey]} ${formatTargetMs(row.targetMs, row.useBusinessHours)}`)
    .join(' · ');

  return (
    <tr
      draggable={draggable}
      onDragStart={(e) => onDragStart(e, policy.id)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, policy.id)}
      style={{ borderTop: '1px solid var(--theme-card-border)', cursor: draggable ? 'grab' : 'default' }}
      data-testid={`sla-policy-row-${policy.id}`}
    >
      <td className="px-4 py-3 w-10">
        <Checkbox checked={selected} onChange={() => onToggleSelect(policy.id)} aria-label={`Selecionar ${policy.name}`} />
      </td>

      <td className="px-2 py-3 w-10">
        {draggable ? (
          <Tooltip content="Arraste para mudar a ordem de avaliação">
            <span className="inline-flex" data-testid={`sla-policy-drag-${policy.id}`}>
              <Icon name="drag" size={16} style={{ color: 'var(--theme-muted-text)', cursor: 'grab' }} />
            </span>
          </Tooltip>
        ) : (
          <Tooltip
            content={
              dragDisabledReason
                ?? (policy.isDefault ? 'A política padrão é sempre a última avaliada' : 'Política inativa não é avaliada')
            }
          >
            <span className="inline-flex">
              <Icon
                name={policy.isDefault ? 'star' : 'lock'}
                size={16}
                style={{ color: policy.isDefault ? 'var(--theme-warning-fg)' : 'var(--theme-muted-text)' }}
              />
            </span>
          </Tooltip>
        )}
      </td>

      <td className={`${CELL} tabular-nums w-12`} style={{ color: 'var(--theme-muted-text)' }}>
        {positionHiddenReason ? (
          <Tooltip content={positionHiddenReason}>
            <span data-testid={`sla-policy-position-hidden-${policy.id}`}>—</span>
          </Tooltip>
        ) : (
          policy.status === 'active' ? position || '—' : '—'
        )}
      </td>

      <td className={CELL}>
        <div className="font-semibold truncate" style={{ color: 'var(--theme-text)' }}>{policy.name}</div>
        <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>{summary}</div>
      </td>

      <td className={CELL} style={{ color: 'var(--theme-card-text)' }}>
        {policy.entityType === 'ticket' ? 'Chamados' : 'Conversas'}
      </td>

      <td className={CELL}>
        <div className="flex items-center gap-2">
          <StatusPill
            variant={policy.status === 'active' ? 'success' : 'neutral'}
            label={policy.status === 'active' ? 'Ativa' : 'Inativa'}
            dot
          />
          {policy.isDefault && <StatusPill variant="info" label="Padrão" />}
        </div>
      </td>

      <td className="px-4 py-3 w-14 text-right">
        <CardActionsMenu
          size="sm"
          data-testid={`sla-policy-actions-${policy.id}`}
          actions={[
            { label: 'Editar', icon: 'edit', onClick: () => onEdit(policy), disabled: !canUpdate },
            {
              label: 'Tornar padrão',
              icon: 'star',
              onClick: () => onSetDefault(policy),
              // Espelho do guard 3 do `SlaPoliciesService.setDefault` (409
              // ConflictError): política INATIVA não pode virar padrão — a
              // padrão é o piso da cascata e o piso precisa ser avaliável.
              // Sem o espelho o item ficava clicável e o usuário levava o
              // erro do servidor por uma condição que a tela já conhecia.
              disabled: !canUpdate || policy.isDefault || policy.status !== 'active',
              ...(policy.status !== 'active' && !policy.isDefault
                ? { disabledReason: 'Política inativa não pode virar padrão — ative-a primeiro.' }
                : {}),
            },
            {
              label: 'Excluir',
              icon: 'trash',
              variant: 'danger',
              dividerBefore: true,
              // A padrão nunca é excluível: sem ela a cascata fica sem piso.
              onClick: () => onDelete(policy),
              disabled: !canDelete || policy.isDefault,
            },
          ]}
        />
      </td>
    </tr>
  );
};

SlaPolicyListItem.displayName = 'SlaPolicyListItem';
